import { useId } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ModalBottom } from "src/domains/shared/components/ModalBottom";
import { ModalHeader } from "src/domains/shared/components/ModalHeader";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  TYPOGRAPHY_SCALE,
} from "src/domains/shared/constants/styles";
import { deviceConflictTestIds } from "src/test/testIds";

export interface DeviceConflictDialogProps {
  /** 다른 기기가 이 계정을 쓰고 있어 로그인이 멈춘 동안 연다 */
  open: boolean;
  /** "로그인" 응답을 기다리는 중 — 두 버튼과 헤더 닫기가 비활성이고 어떤 경로로도 닫히지 않는다 */
  confirming: boolean;
  /** "로그인" — 다른 기기를 밀어내고 로그인을 마친다 */
  onConfirm: () => void;
  /** "취소" — 헤더 닫기·Esc·배경 클릭도 같은 취소다 */
  onCancel: () => void;
}

/**
 * 기기 충돌 확인창 — 로그인 직후 점유가 "다른 기기에서 사용 중"(409)을 받으면 뜬다.
 *
 * 문구는 `auth:deviceConflict.*` 이고, 동작은 호출부의 점유 흐름(`useLoginClaimFlow`)이 정한다.
 * 확인 진행 중에는 닫히는 경로를 모두 막는다 — 확인창만 사라지고 로그인이 대기로 남으면 안 된다.
 */
export const DeviceConflictDialog = ({
  open,
  confirming,
  onConfirm,
  onCancel,
}: DeviceConflictDialogProps) => {
  const { t } = useTranslation("auth");
  const titleId = useId();
  const bodyId = useId();
  const closeAsCancel = confirming ? undefined : onCancel;

  return (
    <Dialog
      open={open}
      onClose={closeAsCancel}
      // role="dialog" 요소의 접근명은 헤더의 제목 헤딩(h2)이, 설명은 본문이 준다.
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      data-testid={deviceConflictTestIds.root}
      slotProps={{ paper: { sx: STYLES.paper } }}
    >
      <ModalHeader
        size="medium"
        leftSlot
        title={t("deviceConflict.title")}
        titleComponent="h2"
        titleId={titleId}
        onClose={closeAsCancel}
        closeButtonProps={{
          disabled: confirming,
          "data-testid": deviceConflictTestIds.closeBtn,
        }}
        sx={STYLES.header}
      />
      <Box sx={STYLES.body}>
        <Typography id={bodyId} sx={STYLES.bodyText}>
          {t("deviceConflict.body")}
        </Typography>
      </Box>
      <ModalBottom
        intent="medium"
        leftButtonText={t("deviceConflict.cancel")}
        onLeftButtonClick={onCancel}
        leftButtonProps={{
          disabled: confirming,
          // 열리면 포커스를 확인창 안으로 — 덜 위험한 쪽(취소)에 둔다. 무심코 누른 Enter 가
          // 다른 기기를 밀어내지 않게 한다.
          autoFocus: true,
          "data-testid": deviceConflictTestIds.cancelBtn,
        }}
        rightButtonText={t("deviceConflict.confirm")}
        onRightButtonClick={onConfirm}
        rightButtonProps={{
          loading: confirming,
          "data-testid": deviceConflictTestIds.confirmBtn,
        }}
      />
    </Dialog>
  );
};

/** 로그인 카드의 최대 폭과 같다 — 확인창이 카드와 나란히 맞는다. */
const DIALOG_WIDTH = "480px";

const STYLES = {
  paper: {
    width: DIALOG_WIDTH,
    maxWidth: `calc(100vw - ${SPACING_CONSTANTS.spacing[8]}px)`,
    borderRadius: SPACING_CONSTANTS.borderRadius.lg,
  },
  header: {
    gap: `${SPACING_CONSTANTS.spacing[2]}px`,
  },
  body: {
    padding: `0 ${SPACING_CONSTANTS.spacing[4]}px`,
  },
  bodyText: {
    ...TYPOGRAPHY_SCALE.body2,
    color: COLOR_CONSTANTS.text.secondary,
    // 한국어 본문이 어절 중간에서 끊기지 않게 한다(공용 FolderTree 메시지와 같은 줄바꿈 규칙).
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
  },
} as const;
